import { useState } from "react";
import { IconBug, IconCirclePlus } from "@tabler/icons-react";
import ModalShell from "./ModalShell";
import styles from "./SupportModal.module.css";
import Button from "../UI/Button/button";
import Input, { Select, Textarea } from "../UI/FormInput/input";
import { postSupportComplaint } from "../../services/clientCalls";
import { errorHandler } from "../../utils";

const PAGES = [
  "Dashboard",
  "My Jobs",
  "Talents",
  "Messages",
  "Company Profile",
  "Subscription",
  "Other",
];
const SEVERITIES = [
  { value: "low", label: "Low — minor inconvenience" },
  { value: "medium", label: "Medium — blocks a feature" },
  { value: "high", label: "High — can't use the platform" },
];

const EMPTY = {
  supportType: "LogABug",
  fullName: "",
  email: "",
  pageOrFeature: "",
  message: "",
  severity: "low",
};

export default function BugModal({ open, onClose, setToast }) {
  const [form, setForm] = useState(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleClose = () => {
    setForm(EMPTY);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        supportType: "LogABug",
        fullName: form.fullName,
        email: form.email,
        pageOrFeature: form.pageOrFeature,
        severity: form.severity,
        message: form.message,
      };
      const res = await postSupportComplaint(payload);
      if (res?.data?.success) {
        handleClose();
        setToast({
          visible: true,
          message: "Bug logged! Our engineering team has been notified.",
        });
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      icon={IconBug}
      color="amber"
      title="Log a Bug"
      subtitle="Help us improve by describing the issue"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={<IconCirclePlus size={14} />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Submit Bug
          </Button>
        </>
      }
    >
      <div className={styles.row2}>
        <Input
          label="Your Name"
          required
          id="bug-name"
          placeholder="Full name"
          value={form.fullName}
          onChange={set("fullName")}
        />
        <Input
          label="Email"
          required
          id="bug-email"
          type="email"
          placeholder="you@company.com"
          value={form.email}
          onChange={set("email")}
        />
      </div>

      <div className={styles.row2}>
        <Select
          label="Page / Feature"
          required
          id="bug-page"
          value={form.pageOrFeature}
          onChange={set("pageOrFeature")}
        >
          <option value="" disabled>
            Where did it happen?
          </option>
          {PAGES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </Select>

        <Select
          label="Severity"
          id="bug-severity"
          value={form.severity}
          onChange={set("severity")}
        >
          {SEVERITIES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
      </div>

      <Textarea
        label="What happened?"
        required
        id="bug-description"
        placeholder="Describe what you did, what you expected to happen, and what actually happened…"
        value={form.message}
        onChange={set("message")}
      />
    </ModalShell>
  );
}
